// Domain-specific console enhancements for padelmaster.club
// This file contains special features for the production domain

import { shouldShowProductionBanner } from '../config/domains';
import { apiMonitor, checkApiStatus } from './apiStatusMonitor';
import { lerAccessToken } from '@/security/tokens';

// Extended type for production features
interface PadelMasterExtended {
  version: {
    name: string;
    version: string;
    build: string;
    date: string;
    domain: string;
    author: string;
    backend: string;
    frontend: string;
  };
  showVersion: () => void;
  showStats: () => void;
  clearData: () => void;
  debug: boolean;
  console: {
    banner: () => void;
    developerInfo: () => void;
  };
  production?: {
    checkStatus: () => void;
    checkAllApis: () => Promise<void>;
    monitorApis: (intervalSeconds?: number) => void;
    stopMonitoring: () => void;
    getSystemHealth: () => { overall: number; critical: number; status: string; };
    testApis: () => Promise<{ dashboard: boolean; auth: boolean; games: boolean; }>;
    reportIssue: () => void;
    shortcuts: () => void;
  };
}

export const DOMAIN_CONFIG = {
  production: 'padelmaster.club',
  productionAlt: 'padelmas.club', 
  staging: 'staging.padelmaster.club',
  development: 'localhost'
};

export const createProductionBanner = async () => {
  // Use centralized domain detection
  if (!shouldShowProductionBanner()) {
    return;
  }
    
    // Special production banner
    console.log(
      '%c🏆 PADELMASTER.CLUB - SITE OFICIAL 🏆',
      `
        background: linear-gradient(45deg, #10b981, #059669);
        color: white;
        font-weight: bold;
        font-size: 20px;
        padding: 15px 30px;
        border-radius: 10px;
        text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        border: 2px solid #065f46;
      `
    );

    // Authentication status
    const isAuthenticated = lerAccessToken();
    if (isAuthenticated) {
      console.log(
        '%c✅ Usuário Autenticado',
        'color: #16a34a; font-weight: bold; font-size: 14px;'
      );
    } else {
      console.log(
        '%c🔑 Entre em sua conta para acessar todas as funcionalidades',
        'color: #ea580c; font-weight: bold; font-size: 14px;'
      );
    }

    // Live features status with API monitoring
    console.log('%c🚀 Status dos Sistemas:', 'color: #1e40af; font-weight: bold; font-size: 16px;');
    
    // Quick API connectivity check
    console.log('%c   🔍 Verificando APIs...', 'color: #6b7280; font-size: 12px;');
    
    // Check backend connectivity with detailed API status
    await checkBackendStatusDetailed();
    
    // Check localStorage availability
    const storageAvailable = checkStorageAvailability();
    console.log(`%c   Armazenamento Local: ${storageAvailable ? '🟢 Disponível' : '🔴 Indisponível'}`, 'color: #374151; font-size: 14px;');
    
    // Game systems status
    console.log('%c   Sistemas de Jogo:', 'color: #374151; font-size: 14px;');
    console.log('%c     ├─ 8 Jogadores: 🟢 Ativo', 'color: #374151; font-size: 12px;');
    console.log('%c     ├─ 12 Jogadores: 🟢 Ativo', 'color: #374151; font-size: 12px;');
    console.log('%c     └─ 16 Jogadores: � Em Desenvolvimento', 'color: #374151; font-size: 12px;');

    // Security notice
    console.log(
      '%c🔒 AVISO DE SEGURANÇA',
      'color: #dc2626; font-weight: bold; font-size: 16px; background: #fef2f2; padding: 5px;'
    );
    console.log(
      '%cEste é o console do desenvolvedor. Não cole códigos de terceiros aqui!',
      'color: #991b1b; font-weight: bold; font-size: 14px;'
    );
    console.log(
      '%cSe alguém pediu para você colar algo aqui, pode ser uma tentativa de hack.',
      'color: #991b1b; font-size: 13px;'
    );

    // Performance tracking
    displayPerformanceMetrics();
    
    // Easter egg
    displayEasterEgg();
};

const checkBackendStatusDetailed = async (): Promise<void> => {
  try {
    // Check main health endpoint first
    const healthResponse = await fetch('/health/', { 
      signal: AbortSignal.timeout(3000) 
    });
    
    if (healthResponse.ok) {
      console.log('%c   Backend API: 🟢 Online', 'color: #16a34a; font-size: 14px; font-weight: bold;');
      
      // Quick check of critical APIs
      console.log('%c   ├─ Dashboard: 🟢 Disponível', 'color: #374151; font-size: 12px;');
      console.log('%c   ├─ Autenticação: 🟢 Disponível', 'color: #374151; font-size: 12px;');
      console.log('%c   └─ Jogos: 🟢 Disponível', 'color: #374151; font-size: 12px;');
      
      // Suggest full API check
      console.log('%c   💡 Digite "PadelMaster.production.checkAllApis()" para verificação completa', 'color: #6b7280; font-size: 11px;');
    } else {
      console.log('%c   Backend API: 🔴 Offline', 'color: #dc2626; font-size: 14px; font-weight: bold;');
    }
  } catch (error) {
    console.log('%c   Backend API: 🔴 Offline', 'color: #dc2626; font-size: 14px; font-weight: bold;');
    console.log('%c   └─ Modo Offline ativo', 'color: #6b7280; font-size: 11px;');
  }
};

const checkBackendStatus = (): boolean => {
  // Try to check if we have recent successful API calls
  try {
    const lastApiCall = localStorage.getItem('last_successful_api_call');
    if (lastApiCall) {
      const timeDiff = Date.now() - parseInt(lastApiCall);
      return timeDiff < 5 * 60 * 1000; // Last 5 minutes
    }
    return false;
  } catch {
    return false;
  }
};

const checkStorageAvailability = (): boolean => {
  try {
    localStorage.setItem('test', 'test');
    localStorage.removeItem('test');
    return true;
  } catch {
    return false;
  }
};

const displayPerformanceMetrics = () => {
  if (performance && performance.timing) {
    const timing = performance.timing;
    const loadTime = timing.loadEventEnd - timing.navigationStart;
    const domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
    const firstPaint = performance.getEntriesByType('paint').find(entry => entry.name === 'first-paint');
    
    console.log('%c📊 Métricas de Performance:', 'color: #7c3aed; font-weight: bold; font-size: 16px;');
    console.log(`%c   ├─ Carregamento Total: ${loadTime}ms`, 'color: #374151; font-size: 14px;');
    console.log(`%c   ├─ DOM Content Loaded: ${domContentLoaded}ms`, 'color: #374151; font-size: 14px;');
    
    if (firstPaint) {
      console.log(`%c   └─ First Paint: ${Math.round(firstPaint.startTime)}ms`, 'color: #374151; font-size: 14px;');
    }

    // Performance rating
    if (loadTime < 2000) {
      console.log('%c⚡ Performance: EXCELENTE', 'color: #16a34a; font-weight: bold;');
    } else if (loadTime < 4000) {
      console.log('%c🟡 Performance: BOA', 'color: #ea580c; font-weight: bold;');
    } else {
      console.log('%c🔴 Performance: PRECISA MELHORAR', 'color: #dc2626; font-weight: bold;');
    }
  }
};

const displayEasterEgg = () => {
  const now = new Date();
  const isChristmas = now.getMonth() === 11 && now.getDate() === 25;
  const isNewYear = now.getMonth() === 0 && now.getDate() === 1;
  const isBirthday = now.getMonth() === 6 && now.getDate() === 31; // Assuming July 31 is project birthday
  
  if (isChristmas) {
    console.log('%c🎄 Feliz Natal! Que 2026 seja cheio de vitórias no padel! 🎄', 'color: #dc2626; font-size: 16px; font-weight: bold;');
  } else if (isNewYear) {
    console.log('%c🎊 Feliz Ano Novo! Que este ano seja repleto de grandes jogos! 🎊', 'color: #7c3aed; font-size: 16px; font-weight: bold;');
  } else if (isBirthday) {
    console.log('%c🎂 Aniversário do PadelMaster! Obrigado por fazer parte desta jornada! 🎂', 'color: #ea580c; font-size: 16px; font-weight: bold;');
  }

  // Random motivational message
  const motivationalMessages = [
    '🔥 Sua próxima vitória está a um jogo de distância!',
    '🏆 Grandes campeões começaram com um único jogo!',
    '⚡ A persistência é a chave para a maestria no padel!',
    '🎯 Cada ponto perdido é uma lição aprendida!',
    '🚀 O único limite é o seu próprio potencial!'
  ];
  
  const randomMotivation = motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
  
  setTimeout(() => {
    console.log(`%c${randomMotivation}`, 'color: #16a34a; font-size: 14px; font-weight: bold; font-style: italic;');
  }, 2000);
};

// Function to track user interactions for analytics
export const trackConsoleUsage = () => {
  // Detect when console is opened (hacky but works)
  const startTime = Date.now();
  console.log('%c', ''); // Invisible log to trigger console
  
  if (Date.now() - startTime > 100) {
    // Log console usage (could be sent to analytics)
    console.log('%c👀 Console aberto detectado!', 'color: #6b7280; font-size: 12px;');
    console.log('%c💡 Digite "PadelMaster" no console para ver comandos disponíveis!', 'color: #6b7280; font-size: 12px;');
  }
};

// Enhanced global object for production
export const setupProductionGlobals = () => {
  if (shouldShowProductionBanner()) {
    
    // Add production-specific methods
    if (window.PadelMaster) {
      // Type assertion to add production property
      (window.PadelMaster as PadelMasterExtended).production = {
        checkStatus: () => {
          console.log('%c📊 Status do Sistema:', 'color: #1e40af; font-weight: bold;');
          console.log(`%c   Backend: ${checkBackendStatus() ? '🟢' : '🔴'}`, 'color: #374151;');
          console.log(`%c   Storage: ${checkStorageAvailability() ? '🟢' : '🔴'}`, 'color: #374151;');
        },
        
        checkAllApis: async () => {
          console.log('%c🔍 Verificação completa de APIs...', 'color: #1e40af; font-weight: bold;');
          await checkApiStatus();
        },
        
        monitorApis: (intervalSeconds: number = 30) => {
          console.log(`%c🔄 Iniciando monitoramento automático de APIs (${intervalSeconds}s)`, 'color: #059669; font-weight: bold;');
          apiMonitor.startMonitoring(intervalSeconds);
        },
        
        stopMonitoring: () => {
          apiMonitor.stopMonitoring();
        },
        
        getSystemHealth: () => {
          const health = apiMonitor.getSystemHealth();
          console.table({
            'Status Geral': `${health.overall}%`,
            'APIs Críticas': `${health.critical}%`,
            'Status do Sistema': health.status
          });
          return health;
        },
        
        reportIssue: () => {
          console.log('%c🐛 Para reportar problemas:', 'color: #dc2626; font-weight: bold;');
          console.log('%c   1. Tire um screenshot do erro', 'color: #374151;');
          console.log('%c   2. Execute PadelMaster.production.getSystemHealth()', 'color: #374151;');
          console.log('%c   3. Anote os passos que levaram ao problema', 'color: #374151;');
          console.log('%c   4. Entre em contato via suporte', 'color: #374151;');
        },
        
        shortcuts: () => {
          console.log('%c⌨️ Atalhos Úteis:', 'color: #7c3aed; font-weight: bold;');
          console.log('%c   Ctrl+Shift+I: Abrir DevTools', 'color: #374151;');
          console.log('%c   Ctrl+Shift+C: Inspecionar elemento', 'color: #374151;');
          console.log('%c   Ctrl+F5: Atualizar ignorando cache', 'color: #374151;');
          console.log('%c   F12: Abrir/fechar console', 'color: #374151;');
        },
        
        testApis: async () => {
          console.log('%c🧪 Executando testes de API...', 'color: #7c3aed; font-weight: bold;');
          
          const testResults = {
            dashboard: false,
            auth: false,
            games: false
          };
          
          try {
            // Test dashboard
            const dashResponse = await fetch('/padel/games/dashboard/games/', { 
              signal: AbortSignal.timeout(5000) 
            });
            testResults.dashboard = dashResponse.ok;
            
            // Test auth (if token exists)
            const token = lerAccessToken();
            if (token) {
              const authResponse = await fetch('/auth/user/profile/', {
                headers: { 'Authorization': `Bearer ${token}` },
                signal: AbortSignal.timeout(5000)
              });
              testResults.auth = authResponse.ok;
            }
            
            // Test game save
            const gameResponse = await fetch('/padel/games/8players/saveGame/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ test: true }),
              signal: AbortSignal.timeout(5000)
            });
            testResults.games = gameResponse.status !== 500; // Accept non-500 errors for test
            
          } catch (error) {
            console.log('%c⚠️ Alguns testes falharam - verifique a conectividade', 'color: #ea580c;');
          }
          
          console.table(testResults);
          return testResults;
        }
      };
    }
    
    // Track console usage
    trackConsoleUsage();
  }
};
